import styled from '@emotion/styled'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import catchError from '../../components/global/CatchError'
import Loader from '../../components/global/Loader'
import Title from '../../components/global/Title'
import { sweetAlertSuccess } from '../../components/sweetalert/Alert'
import UserContext from '../../context/UserContext'
import { useLangSelected } from '../../hooks/useLangSelected'
import { useVerifyAdminToken } from '../../hooks/useVerifyAdminToken'
import apiClient from '../../services/apiClient'
import { serverFile } from '../../services/file.service'
import { AdminContainer, LinkBtn, TitleWrapper } from './users/AdminUsers'

interface CarouselImage {
    id: number
    imageUrl: string
    order: number
    isActive: boolean
}

interface CarouselSettings {
    id: number
    intervalSeconds: number
    transition: string
}

const AdminCarousel = () => {
    const user = useContext(UserContext)
    const [images, setImages] = useState<CarouselImage[] | undefined>(undefined)
    const [settings, setSettings] = useState<CarouselSettings | undefined>(undefined)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [newOrder, setNewOrder] = useState<{ [key: number]: number | undefined }>({})

    const verifyToken = useVerifyAdminToken()
    const lng = useLangSelected()
    const { t } = useTranslation()

    useEffect(() => {
        verifyToken()
        loadData()
    }, [lng])

    const loadData = () => {
        apiClient
            .get('/carousel/images')
            .then(({ data }) => {
                setImages(data)
            })
            .catch((err) =>
                catchError(err.response.data, () => {
                    window.location.reload()
                }),
            )

        apiClient
            .get('/carousel/settings')
            .then(({ data }) => {
                setSettings(data)
            })
            .catch((err) =>
                catchError(err.response.data, () => {
                    window.location.reload()
                }),
            )
    }

    if (!images || !settings) return <Loader />

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0])
        }
    }

    const handleAddImage = () => {
        if (!selectedFile) return

        const formData = new FormData()
        formData.append('image', selectedFile)
        formData.append('order', String(images.length))

        verifyToken()
        apiClient
            .post('/carousel/image', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            })
            .then(() => {
                sweetAlertSuccess(t('admin.carousel.addImageSuccess')).then(() => {
                    setSelectedFile(null)
                    loadData()
                })
            })
            .catch((err) =>
                catchError(err.response.data, () => {
                    window.location.reload()
                }),
            )
    }

    const handleDeleteImage = (id: number) => {
        verifyToken()
        apiClient
            .delete(`/carousel/image/${id}`)
            .then(() => {
                sweetAlertSuccess(t('admin.carousel.deleteImageSuccess')).then(() => {
                    loadData()
                })
            })
            .catch((err) =>
                catchError(err.response.data, () => {
                    window.location.reload()
                }),
            )
    }

    const handleToggleActive = (id: number) => {
        verifyToken()
        apiClient
            .patch(`/carousel/image/${id}/active`)
            .then(() => {
                sweetAlertSuccess(t('admin.carousel.toggleActiveSuccess')).then(() => {
                    loadData()
                })
            })
            .catch((err) =>
                catchError(err.response.data, () => {
                    window.location.reload()
                }),
            )
    }

    const handleUpdateOrder = (id: number) => {
        const order = newOrder[id]
        if (order === undefined) return

        verifyToken()
        apiClient
            .patch(`/carousel/image/${id}/order`, { order })
            .then(() => {
                sweetAlertSuccess(t('admin.carousel.updateOrderSuccess')).then(() => {
                    setNewOrder({ ...newOrder, [id]: undefined })
                    loadData()
                })
            })
            .catch((err) =>
                catchError(err.response.data, () => {
                    window.location.reload()
                }),
            )
    }

    const handleUpdateSettings = () => {
        verifyToken()
        apiClient
            .patch('/carousel/settings', {
                intervalSeconds: settings.intervalSeconds,
                transition: settings.transition,
            })
            .then(() => {
                sweetAlertSuccess(t('admin.carousel.updateSettingsSuccess'))
            })
            .catch((err) =>
                catchError(err.response.data, () => {
                    window.location.reload()
                }),
            )
    }

    return (
        <>
            <TitleWrapper>
                <Title>{t('admin.carousel.title')}</Title>
            </TitleWrapper>
            <AdminContainer>
                <Section>
                    <SectionTitle>{t('admin.carousel.settings')}</SectionTitle>
                    <SettingsForm>
                        <FormGroup>
                            <Label>{t('admin.carousel.interval')}</Label>
                            <Input
                                type="number"
                                value={settings.intervalSeconds}
                                onChange={(e) => setSettings({ ...settings, intervalSeconds: Number(e.target.value) })}
                                min="1"
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>{t('admin.carousel.transition')}</Label>
                            <Select
                                value={settings.transition}
                                onChange={(e) => setSettings({ ...settings, transition: e.target.value })}
                            >
                                <option value="fade">{t('admin.carousel.transitionFade')}</option>
                                <option value="slide">{t('admin.carousel.transitionSlide')}</option>
                            </Select>
                        </FormGroup>
                        <SaveButton onClick={handleUpdateSettings}>{t('common.save')}</SaveButton>
                    </SettingsForm>
                </Section>

                <Section>
                    <SectionTitle>{t('admin.carousel.addImage')}</SectionTitle>
                    <AddImageForm>
                        <FileInput type="file" accept="image/*" onChange={handleFileChange} />
                        <AddButton onClick={handleAddImage} disabled={!selectedFile}>
                            {t('admin.carousel.addImage')}
                        </AddButton>
                    </AddImageForm>
                </Section>

                <Section>
                    <SectionTitle>{t('admin.carousel.order')}</SectionTitle>
                    <ImagesList>
                        {images.map((image) => (
                            <ImageItem key={image.id}>
                                <ImagePreview src={serverFile(image.imageUrl)} alt="" />
                                <ImageInfo>
                                    <OrderInput
                                        type="number"
                                        value={newOrder[image.id] !== undefined ? newOrder[image.id] : image.order}
                                        onChange={(e) =>
                                            setNewOrder({ ...newOrder, [image.id]: Number(e.target.value) })
                                        }
                                    />
                                    <UpdateOrderButton
                                        onClick={() => handleUpdateOrder(image.id)}
                                        disabled={newOrder[image.id] === undefined}
                                    >
                                        {t('common.update')}
                                    </UpdateOrderButton>
                                    <ToggleButton
                                        onClick={() => handleToggleActive(image.id)}
                                        isActive={image.isActive}
                                    >
                                        {image.isActive ? t('admin.carousel.active') : t('admin.carousel.inactive')}
                                    </ToggleButton>
                                    <DeleteButton onClick={() => handleDeleteImage(image.id)}>
                                        {t('admin.carousel.delete')}
                                    </DeleteButton>
                                </ImageInfo>
                            </ImageItem>
                        ))}
                    </ImagesList>
                </Section>
            </AdminContainer>
        </>
    )
}

const Section = styled.div`
    margin-bottom: 2rem;
`

const SectionTitle = styled.h3`
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: var(--primary-color, #b40000);
`

const SettingsForm = styled.div`
    display: flex;
    gap: 1rem;
    align-items: flex-end;
    flex-wrap: wrap;
`

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`

const Label = styled.label`
    font-weight: bold;
`

const Input = styled.input`
    padding: 0.5rem;
    border: 1px solid #ccc;
    border-radius: 4px;
    width: 150px;
`

const Select = styled.select`
    padding: 0.5rem;
    border: 1px solid #ccc;
    border-radius: 4px;
    width: 150px;
`

const SaveButton = styled.button`
    padding: 0.5rem 1rem;
    background-color: var(--primary-color, #b40000);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    &:hover {
        background-color: rgba(180, 0, 0, 0.8);
    }
`

const AddImageForm = styled.div`
    display: flex;
    gap: 1rem;
    align-items: center;
`

const FileInput = styled.input`
    padding: 0.5rem;
`

const AddButton = styled.button`
    padding: 0.5rem 1rem;
    background-color: var(--primary-color, #b40000);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    &:hover {
        background-color: rgba(180, 0, 0, 0.8);
    }
    &:disabled {
        background-color: #ccc;
        cursor: not-allowed;
    }
`

const ImagesList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1rem;
`

const ImageItem = styled.div`
    border: 1px solid #ccc;
    border-radius: 8px;
    overflow: hidden;
`

const ImagePreview = styled.img`
    width: 100%;
    height: 200px;
    object-fit: cover;
`

const ImageInfo = styled.div`
    padding: 1rem;
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
`

const OrderInput = styled.input`
    width: 60px;
    padding: 0.25rem;
    border: 1px solid #ccc;
    border-radius: 4px;
`

const UpdateOrderButton = styled.button`
    padding: 0.25rem 0.5rem;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    &:hover {
        background-color: #0056b3;
    }
    &:disabled {
        background-color: #ccc;
        cursor: not-allowed;
    }
`

const ToggleButton = styled.button<{ isActive: boolean }>`
    padding: 0.25rem 0.5rem;
    background-color: ${(props) => (props.isActive ? '#28a745' : '#6c757d')};
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    &:hover {
        background-color: ${(props) => (props.isActive ? '#218838' : '#5a6268')};
    }
`

const DeleteButton = styled.button`
  the padding: 0.25rem 0.5rem;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  &:hover {
    background-color: #c82333;
  }
`

export default AdminCarousel
